/**
 * App lifecycle hooks for Capacitor native environment.
 * - StatusBar styling
 * - SplashScreen management
 * - Hardware back button handling
 * - Keyboard management
 * - Deep links & OAuth callback handling
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const isNative = () => Capacitor.isNativePlatform();

/** Published domain kept for Universal Links / App Links and shared webcredentials */
export const PUBLISHED_URL = 'https://mycue.lovable.app';
export const NATIVE_AUTH_CALLBACK_URL = 'sg.com.mycue.app://auth-callback';
export const NATIVE_POST_AUTH_PENDING_KEY = 'native_post_auth_pending';

const setNativePostAuthPending = (isPending: boolean) => {
  try {
    if (isPending) {
      sessionStorage.setItem(NATIVE_POST_AUTH_PENDING_KEY, '1');
    } else {
      sessionStorage.removeItem(NATIVE_POST_AUTH_PENDING_KEY);
    }
  } catch {}
};

/** Deduplication guard — prevents getLaunchUrl + appUrlOpen from processing the same URL twice */
let _lastHandledOAuthUrl: string | null = null;
let _lastHandledOAuthAt = 0;

/** Detect iPad (including iPhone-only compatibility mode on iPad) */
const isIPadDevice = () => {
  return (
    navigator.userAgent.includes('iPad') ||
    // iPad running iPhone-only app still reports MacIntel with touch
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    // iPadOS 13+ Safari reports itself as desktop
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
};

const isRecognizedOAuthUrl = (url: URL) => {
  const hashParams = new URLSearchParams(url.hash?.replace(/^#/, '') ?? '');
  const isCustomSchemeCallback = url.protocol === 'sg.com.mycue.app:' && (
    url.hostname === 'auth-callback' ||
    url.host === 'auth-callback' ||
    url.pathname === '/auth-callback' ||
    url.pathname.includes('auth-callback')
  );

  return Boolean(
    hashParams.get('access_token') ||
    (hashParams.get('refresh_token') && hashParams.get('access_token')) ||
    url.searchParams.get('access_token') ||
    url.searchParams.get('refresh_token') ||
    url.searchParams.get('code') ||
    url.searchParams.get('error') ||
    isCustomSchemeCallback
  );
};

/**
 * Extract OAuth tokens from a URL hash/query and set the Supabase session.
 * Returns true if tokens were found and session was set.
 */
const handleOAuthCallback = async (urlString: string): Promise<boolean> => {
  try {
    const url = new URL(urlString);
    if (!isRecognizedOAuthUrl(url)) return false;

    setNativePostAuthPending(true);

    const hashParams = new URLSearchParams(url.hash?.replace(/^#/, '') ?? '');
    const accessToken = hashParams.get('access_token') ?? url.searchParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token') ?? url.searchParams.get('refresh_token');
    const code = url.searchParams.get('code') ?? hashParams.get('code');
    const authError = url.searchParams.get('error') ?? hashParams.get('error');
    const authErrorDescription = url.searchParams.get('error_description') ?? hashParams.get('error_description');

    if (authError) {
      console.error('OAuth callback error:', authError, authErrorDescription ?? '');
      setNativePostAuthPending(false);
      return true;
    }

    if (accessToken && refreshToken) {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        console.error('OAuth setSession error:', error.message);
        setNativePostAuthPending(false);
        return false;
      }

      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();
      } catch {}

      return true;
    }

    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        console.error('OAuth code exchange error:', error.message);
        setNativePostAuthPending(false);
        return false;
      }

      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();
      } catch {}

      return true;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();
      } catch {}
      return true;
    }

    setNativePostAuthPending(false);
    return false;
  } catch (error) {
    console.error('OAuth callback parse error:', error);
    setNativePostAuthPending(false);
    return false;
  }
};

/**
 * Configure StatusBar for dark theme, hide SplashScreen after mount.
 * 
 * IMPORTANT: All plugin calls are wrapped in individual try-catch blocks
 * AND have timeouts to prevent hanging on iPad compatibility mode.
 * A global safety timer ensures SplashScreen is always hidden.
 */
export const useNativeAppSetup = () => {
  useEffect(() => {
    if (!isNative()) return;

    let splashHidden = false;

    const hideSplash = async () => {
      if (splashHidden) return;
      splashHidden = true;
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide({ fadeOutDuration: 400 });
      } catch {
        // SplashScreen plugin not available — OK
      }
    };

    const iPad = isIPadDevice();

    // On iPad: hide splash immediately + multiple fallbacks
    // iPad compatibility mode often leaves a transparent overlay blocking touch
    if (iPad) {
      hideSplash();
      const t1 = setTimeout(hideSplash, 800);
      const t2 = setTimeout(() => { splashHidden = false; hideSplash(); }, 2000);

      // Minimal setup for iPad — skip all plugins that might hang
      const setup = async () => {
        try {
          const { StatusBar, Style } = await import('@capacitor/status-bar');
          await Promise.race([
            StatusBar.setStyle({ style: Style.Dark }),
            new Promise((_, r) => setTimeout(r, 2000)),
          ]);
        } catch {}
      };
      setup();

      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    // iPhone path — normal setup
    const safetyTimer = setTimeout(hideSplash, 3000);

    const setup = async () => {
      const platform = Capacitor.getPlatform();

      // StatusBar — dark content, transparent background
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        if (platform === 'android') {
          await StatusBar.setBackgroundColor({ color: '#000000' });
        }
      } catch {}

      // SplashScreen — hide once after app is ready
      await hideSplash();

      // Keyboard — adjust resize behavior
      try {
        const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
        await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      } catch {}
    };

    setup();

    return () => clearTimeout(safetyTimer);
  }, []);
};

/**
 * Handle hardware back button on Android.
 * Goes back in history or exits the app on root routes.
 */
export const useNativeBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isNative()) return;

    let cleanup: (() => void) | undefined;

    const setup = async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = App.addListener('backButton', ({ canGoBack }) => {
          const rootRoutes = ['/', '/discover', '/messages', '/profile'];
          if (rootRoutes.includes(location.pathname)) {
            App.minimizeApp();
          } else if (canGoBack) {
            navigate(-1);
          } else {
            App.minimizeApp();
          }
        });
        cleanup = () => handle.then(h => h.remove());
      } catch {}
    };

    setup();
    return () => { cleanup?.(); };
  }, [navigate, location.pathname]);
};

/**
 * Handle deep links / app URL open events — including OAuth callbacks.
 */
export const useNativeDeepLinks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;

    let cleanupAppUrl: (() => void) | undefined;
    let cleanupBrowserFinished: (() => void) | undefined;
    let isCancelled = false;

    const handleIncomingUrl = async (incomingUrl?: string | null) => {
      if (!incomingUrl || isCancelled) return;

      // Deduplicate: getLaunchUrl and appUrlOpen often fire for the same URL
      if (
        _lastHandledOAuthUrl === incomingUrl &&
        Date.now() - _lastHandledOAuthAt < 5000
      ) return;

      const wasOAuth = await handleOAuthCallback(incomingUrl);
      if (isCancelled) return;

      if (wasOAuth) {
        _lastHandledOAuthUrl = incomingUrl;
        _lastHandledOAuthAt = Date.now();
        // Route all native OAuth completions through /login so one resolver
        // decides whether the user should land on onboarding or home.
        if (window.location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        return;
      }

      try {
        const url = new URL(incomingUrl);
        const path = `${url.pathname}${url.search}${url.hash}`;
        if (path && path !== '/') {
          navigate(path, { replace: true });
        }
      } catch {}
    };

    const setup = async () => {
      try {
        const { App } = await import('@capacitor/app');

        // Critical for TestFlight / cold start: callback may arrive before appUrlOpen listener is ready.
        const launchUrl = await App.getLaunchUrl();
        if (launchUrl?.url) {
          await handleIncomingUrl(launchUrl.url);
        }

        const handle = App.addListener('appUrlOpen', (data) => {
          void handleIncomingUrl(data.url);
        });
        cleanupAppUrl = () => handle.then(h => h.remove());
      } catch {}

      try {
        const { Browser } = await import('@capacitor/browser');
        const handle = Browser.addListener('browserFinished', () => {
          // The OAuth callback should have already been handled via appUrlOpen
          // This is a safety net — check current URL for tokens
        });
        cleanupBrowserFinished = () => handle.then(h => h.remove());
      } catch {}
    };

    void setup();
    return () => {
      isCancelled = true;
      cleanupAppUrl?.();
      cleanupBrowserFinished?.();
    };
  }, [navigate]);
};
